import React, { useCallback, useMemo, useState } from 'react';
import { Column } from 'react-table';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

import { Checkbox, Heading, Panel, Table } from '@savgroup-front-common/core';

import BulkAction from '../components/BulkAction';
import DossierBulkActionDrawer from '../components/DossierBulkActionDrawer';
import { DossierSelectionProvider, useDossierSelection } from './DossierPage.context';

export interface DossierRow {
  numeroDossier: string;
  dateCreation: string;
  dateDerniereModif: string;
  statut: string;
  motif: string;
  garantie: string;
  intervenant: string;
}

const MOCK_DOSSIERS: DossierRow[] = [
  {
    numeroDossier: 'DOS-2024-001',
    dateCreation: '2024-01-15',
    dateDerniereModif: '2024-02-20',
    statut: 'En cours',
    motif: 'Panne matériel',
    garantie: 'Oui',
    intervenant: 'Martin Dupont',
  },
  {
    numeroDossier: 'DOS-2024-002',
    dateCreation: '2024-01-18',
    dateDerniereModif: '2024-02-22',
    statut: 'Clôturé',
    motif: 'Maintenance préventive',
    garantie: 'Non',
    intervenant: 'Sophie Bernard',
  },
  {
    numeroDossier: 'DOS-2024-003',
    dateCreation: '2024-02-01',
    dateDerniereModif: '2024-02-25',
    statut: 'En attente',
    motif: 'Installation',
    garantie: 'Oui',
    intervenant: 'Pierre Martin',
  },
  {
    numeroDossier: 'DOS-2024-004',
    dateCreation: '2024-02-05',
    dateDerniereModif: '2024-02-26',
    statut: 'En cours',
    motif: 'Dépannage urgent',
    garantie: 'Non',
    intervenant: 'Julie Leroy',
  },
  {
    numeroDossier: 'DOS-2024-005',
    dateCreation: '2024-02-10',
    dateDerniereModif: '2024-02-27',
    statut: 'Clôturé',
    motif: 'Contrôle technique',
    garantie: 'Oui',
    intervenant: 'Thomas Petit',
  },
];

const formatDate = (dateStr: string): string => {
  try {
    return format(new Date(dateStr), 'dd/MM/yyyy', { locale: fr });
  } catch {
    return dateStr;
  }
};

const checkboxCellStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 40,
  width: '100%',
};

const SelectionHeaderCheckbox = () => {
  const { allSelected, someSelected, toggleSelectAll } = useDossierSelection();
  return (
    <div style={checkboxCellStyle}>
      <Checkbox
        name="select-all-dossiers"
        checked={allSelected}
        isIndeterminate={someSelected}
        onChange={() => toggleSelectAll()}
        ariaLabel="Tout sélectionner"
        dataTestId="dossiers-select-all"
        withoutMargin
      />
    </div>
  );
};

const SelectionRowCheckbox = ({ numeroDossier }: { numeroDossier: string }) => {
  const { isSelected, toggleSelection } = useDossierSelection();
  const checked = isSelected(numeroDossier);
  return (
    <div style={checkboxCellStyle}>
      <Checkbox
        name={`select-dossier-${numeroDossier}`}
        checked={checked}
        onChange={() => toggleSelection(numeroDossier)}
        ariaLabel={`Sélectionner le dossier ${numeroDossier}`}
        dataTestId={`dossiers-select-${numeroDossier}`}
        withoutMargin
      />
    </div>
  );
};

const DossierPageContent = () => {
  const { selectedIds, deselectAll } = useDossierSelection();
  const [isBulkDrawerOpen, setIsBulkDrawerOpen] = useState(false);

  const centeredCellProps = {
    style: { verticalAlign: 'middle' as const },
  };

  const selectionCellProps = {
    style: { textAlign: 'center' as const, verticalAlign: 'middle' as const },
  };

  const columns = useMemo<Column<DossierRow>[]>(
    () => [
      {
        id: 'selection',
        width: 48,
        disableSortBy: true,
        getProps: () => selectionCellProps,
        Header: () => <SelectionHeaderCheckbox />,
        Cell: ({ row }: { row: { original: DossierRow } }) => (
          <SelectionRowCheckbox numeroDossier={row.original.numeroDossier} />
        ),
      },
      {
        Header: 'N° dossier',
        accessor: 'numeroDossier',
        getProps: () => centeredCellProps,
      },
      {
        Header: 'Date de création',
        accessor: 'dateCreation',
        Cell: ({ value }: { value: string }) => formatDate(value),
        getProps: () => centeredCellProps,
      },
      {
        Header: 'Date dernière modif.',
        accessor: 'dateDerniereModif',
        Cell: ({ value }: { value: string }) => formatDate(value),
        getProps: () => centeredCellProps,
      },
      {
        Header: 'Statut',
        accessor: 'statut',
        getProps: () => centeredCellProps,
      },
      {
        Header: 'Motif',
        accessor: 'motif',
        getProps: () => centeredCellProps,
      },
      {
        Header: 'Garantie',
        accessor: 'garantie',
        getProps: () => centeredCellProps,
      },
      {
        Header: 'Intervenant',
        accessor: 'intervenant',
        getProps: () => centeredCellProps,
      },
    ],
    [],
  );

  const handleBulkAction = useCallback(() => {
    setIsBulkDrawerOpen(true);
  }, []);

  const handleCloseBulkDrawer = useCallback(() => {
    setIsBulkDrawerOpen(false);
  }, []);

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <Panel>
        <Heading level={2}>Liste des dossiers</Heading>
        <Table<DossierRow>
          columns={columns}
          data={MOCK_DOSSIERS}
          dataTestId="dossiers-table"
          showSearch
          searchPlaceholder={{
            id: 'dossier.search',
            defaultMessage: 'Rechercher un dossier...',
          }}
        />
        {selectedIds.size > 0 && (
          <BulkAction
            count={selectedIds.size}
            deselectAll={deselectAll}
            buttonMessage={{
              id: 'dossier.bulkAction',
              defaultMessage: 'Suivant',
            }}
            onClick={handleBulkAction}
            selectedLabel={`${selectedIds.size} dossier(s) sélectionné(s)`}
          />
        )}
      </Panel>
      <DossierBulkActionDrawer
        isOpen={isBulkDrawerOpen}
        onClose={handleCloseBulkDrawer}
        selectedCount={selectedIds.size}
      />
    </div>
  );
};

const DossierPage = () => {
  const allDossierIds = useMemo(
    () => MOCK_DOSSIERS.map((d) => d.numeroDossier),
    [],
  );

  return (
    <DossierSelectionProvider allDossierIds={allDossierIds}>
      <DossierPageContent />
    </DossierSelectionProvider>
  );
};

export default DossierPage;
